import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Eye } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import PolicyDetailView from '../components/policy/PolicyDetailView';
import PolicyForm from '../components/policy/PolicyForm';
import PolicyDocumentsSection from '../components/policy/PolicyDocumentsSection';
import { policiesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { policyToForm, formToPayload } from '../config/policyFieldConfig';

const EDIT_ROLES = ['super_admin', 'hr_manager'];

export default function PolicyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const canEdit = EDIT_ROLES.includes(user?.role);

  const load = async () => {
    const [{ data: p }, { data: prov }] = await Promise.all([
      policiesAPI.get(id),
      policiesAPI.providers(),
    ]);
    setPolicy(p);
    setForm(policyToForm(p));
    setProviders(prov);
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await policiesAPI.update(id, formToPayload(form));
      await load();
      setEditing(false);
      setMessage('Policy updated successfully.');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to update policy');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(policyToForm(policy));
    setEditing(false);
    setMessage('');
  };

  if (loading) return <LoadingSpinner />;
  if (!policy) return <p className="text-slate-500">Policy not found.</p>;

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link to="/policies" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary-600">
          <ArrowLeft className="w-4 h-4" /> All Policies
        </Link>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              editing ? 'bg-slate-100 text-slate-700' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
            }`}
          >
            {editing ? <><Eye className="w-4 h-4" /> View Mode</> : <><Pencil className="w-4 h-4" /> Edit Mode</>}
          </button>
        )}
      </div>

      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm ${message.includes('success') ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      {editing && canEdit ? (
        <div>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Edit Policy</h1>
            <p className="text-slate-500">{policy.company_name} — {policy.policy_number}</p>
          </div>
          <PolicyForm
            form={form}
            onChange={setForm}
            providers={providers}
            onSubmit={handleSave}
            onCancel={handleCancel}
            submitLabel={saving ? 'Saving...' : 'Update Policy'}
          />
        </div>
      ) : (
        <>
          <PolicyDetailView
            policy={policy}
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
          />
          <PolicyDocumentsSection policyId={id} canManage={canEdit} />
        </>
      )}
    </div>
  );
}
